import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "../../lib/api-client";

// The filter set an export applies. Mirrors the leads-list query params; arrays
// become repeated params (?city=A&city=B) so comma-bearing values survive.
export interface LeadExportParams {
  status?: string;
  source?: string;
  assignedTo?: string;
  campaign?: string;
  city?: string[];
  state?: string[];
  q?: string;
  from?: string;
  to?: string;
}

// Build the GET URL for a leads export. Auth is cookie-based and same-origin,
// so a plain navigation to this URL downloads the file (the response carries
// Content-Disposition: attachment) without needing a fetch + blob dance.
export function buildLeadsExportUrl(
  workspaceSlug: string | null,
  params: LeadExportParams
): string {
  const url = new URL("/api/exports/leads", window.location.origin);
  const append = (k: string, v: string | undefined) => {
    if (v) url.searchParams.set(k, v);
  };
  if (workspaceSlug) url.searchParams.set("workspace", workspaceSlug);
  append("status", params.status);
  append("source", params.source);
  append("assignedTo", params.assignedTo);
  append("campaign", params.campaign);
  append("q", params.q);
  append("from", params.from);
  append("to", params.to);
  for (const c of params.city ?? []) url.searchParams.append("city", c);
  for (const s of params.state ?? []) url.searchParams.append("state", s);
  return url.pathname + url.search;
}

// Triggers a browser download for the given export URL via a transient anchor,
// so the SPA stays put (no full-page navigation).
export function triggerDownload(href: string): void {
  const a = document.createElement("a");
  a.href = href;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
}

export function useSoftDeleteLead(workspaceSlug: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (leadId: string) =>
      apiClient.delete(`/api/leads/${leadId}`, {
        params: { workspace: workspaceSlug ?? undefined },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["leads", workspaceSlug] });
    },
  });
}
